<div class="page-edit-profile">

    <h2 class="section-title">Mis datos</h2>

    <div class="content">

        <!-- ===== PERFIL ===== -->
        <section class="card" [formGroup]="userForm">

            <!-- Foto -->
            <div class="row-photo">
                <div class="avatar" (click)="file.click()">
                    <img fallimg *ngIf="imgControl.value"
                        [src]="imgControl.value"
                        alt="Foto de perfil" />
                    <!-- Sin foto, las iniciales: la imagen por defecto era el
                         logo de Citylok y parecía la foto del usuario. -->
                    <span class="avatar-initials" *ngIf="!imgControl.value" aria-hidden="true">{{ initials }}</span>
                    <span class="avatar-camera"><i class="fa-light fa-camera"></i></span>
                </div>
                <!-- Los mismos tres formatos que acepta la API
                     (Functions::getImgAndExtensionFromB64): con `image/*` se
                     podía elegir un GIF o un HEIC y reventaba al guardar. -->
                <input id="file" #file hidden type="file" accept="image/jpeg,image/png,image/webp" (change)="onFileChange($event)">

                <div class="row-body">
                    <p class="m-0 row-name">Foto de perfil</p>
                    <!-- Sin tamaño máximo a propósito: la foto se reduce a
                         1920×1080 y se pasa a JPEG antes de subirla, así que
                         una de 25 MB del móvil entra igual. Lo de los
                         comentarios se quitó: hoy no hay comentarios. -->
                    <!-- WebP también vale, pero no se anuncia: a casi nadie
                         le dice nada y ensucia la frase. -->
                    <p class="m-0 row-help">Puedes subir una foto en JPG o PNG.</p>
                </div>

                <button type="button" class="btn-outline" (click)="file.click()">Cambiar foto</button>
            </div>

            <div class="card-divider"></div>

            <!-- Nombre -->
            <div class="row-field">
                <div class="field-body">
                    <label class="field-label" for="input-name">Nombre y apellidos</label>
                    <input id="input-name" type="text" formControlName="name"
                        [readonly]="!isEditing('name')"
                        [class.is-readonly]="!isEditing('name')" />
                    <span class="field-help" *ngIf="isEditing('name')">Tu nombre será visible para los usuarios de Citylok.</span>
                </div>
                <button type="button" class="btn-icon"
                    [attr.aria-label]="isEditing('name') ? 'Dejar de editar el nombre' : 'Editar nombre'"
                    (click)="toggleEdit('name')">
                    <i class="fa-light" [ngClass]="isEditing('name') ? 'fa-xmark' : 'fa-pen'"></i>
                </button>
            </div>

            <div class="card-divider"></div>

            <!-- Teléfono -->
            <div class="row-field"
                [class.invalid]="isEditing('phone') && userForm.get('phone')?.invalid && userForm.get('phone')?.dirty">
                <div class="field-body">
                    <div class="field-label-row">
                        <label class="field-label" for="input-phone">Teléfono</label>
                        <span class="badge-verified" *ngIf="profile?.sms_validated && !isEditing('phone')">
                            <i class="fa-solid fa-check"></i> VERIFICADO
                        </span>
                    </div>

                    <!-- Leyendo, el teléfono es un texto seguido y agrupado
                         ("+34 666 12 34 56"): con el desplegable del prefijo
                         delante quedaba "+34      666123456". Los controles
                         solo salen al editar. -->
                    <p class="m-0 field-value" *ngIf="!isEditing('phone')">{{ phoneDisplay || '—' }}</p>
                    <div class="field-phone" [class.d-none]="!isEditing('phone')">
                        <div class="country-code" [class.is-active]="isEditing('phone')">
                            <mat-select formControlName="countryCode" class="select-clean">
                                <mat-select-trigger>
                                    <span class="selected-flag-row">+{{ userForm.get('countryCode')?.value }}</span>
                                </mat-select-trigger>
                                <mat-option *ngFor="let country of countryCodes" [value]="country.code">
                                    {{ country.flag }} {{ country.name }} (+{{ country.code }})
                                </mat-option>
                            </mat-select>
                        </div>

                        <input id="input-phone" type="text" formControlName="phone"
                            [readonly]="!isEditing('phone')"
                            [class.is-readonly]="!isEditing('phone')"
                            placeholder="Introduce tu número de teléfono" />
                    </div>

                    <div class="field-error"
                        *ngIf="isEditing('phone') && userForm.get('phone')?.invalid && userForm.get('phone')?.dirty">
                        <span *ngIf="userForm.get('phone')?.errors?.['pattern']">Solo se permiten números</span>
                        <span *ngIf="userForm.get('phone')?.errors?.['minlength']">Mínimo 9 dígitos requeridos</span>
                    </div>
                </div>

                <button type="button" class="btn-icon"
                    [attr.aria-label]="isEditing('phone') ? 'Dejar de editar el teléfono' : 'Editar teléfono'"
                    (click)="toggleEdit('phone')">
                    <i class="fa-light" [ngClass]="isEditing('phone') ? 'fa-xmark' : 'fa-pen'"></i>
                </button>
            </div>

            <div class="card-divider"></div>

            <!-- Correo: no se edita desde aquí. `users.email` es único y es la
                 llave con la que se entra, así que cambiarlo a la ligera deja
                 al usuario fuera de su propia cuenta. -->
            <div class="row-field">
                <div class="field-body">
                    <div class="field-label-row">
                        <label class="field-label" for="input-email">Correo electrónico</label>
                        <span class="badge-verified" *ngIf="profile?.active">
                            <i class="fa-solid fa-check"></i> VERIFICADO
                        </span>
                    </div>
                    <input id="input-email" class="is-readonly" type="email" formControlName="email" readonly>
                </div>
                <span class="field-note">Para cambiarlo, escríbenos desde Ayuda</span>
            </div>
        </section>

        <!-- ===== SEGURIDAD ===== -->
        <section class="card">
            <h3 class="card-title m-0">Seguridad</h3>

            <div class="row-photo">
                <span class="row-icon"><i class="fa-light fa-lock"></i></span>
                <div class="row-body">
                    <p class="m-0 row-name">Contraseña</p>
                    <p class="m-0 row-help">La usas para entrar en Citylok con tu correo.</p>
                </div>
                <button type="button" class="btn-outline" (click)="openChangePassword()">Cambiar</button>
            </div>
        </section>

        <!-- ===== ACCIONES ===== -->
        <div class="actions">
            <button type="button" class="btn-save" *ngIf="changed" (click)="saveChanges()">Guardar cambios</button>
            <div class="actions-spacer"></div>
            <button type="button" class="btn-delete" (click)="deleteAccount()">
                <i class="fa-light fa-circle-exclamation"></i>
                <u>Eliminar mi cuenta</u>
            </button>
        </div>
    </div>
</div>
